<template>
  <div class="full-width d-flex flex-column">
    <!-- 1. Lane selector & export button -->
    <div class="pa-4 d-flex align-center header-row">
      <div class="header-toggles-wrapper">
        <v-btn-toggle
          v-model="selectedLaneIds"
          multiple
          mandatory
          color="primary"
        >
          <v-btn
            v-for="lane in assemblyLanes"
            :key="lane.id"
            :value="lane.id"
            @click="loadTrestlesForLane(lane.id)"
          >
            {{ lane.title }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-spacer />

      <v-btn
        class="ml-4 flex-shrink-0"
        color="primary"
        :loading="exportLoading"
        :disabled="exportLoading || !assemblyLanes.length"
        @click="requestManualExport"
      >
        Export
      </v-btn>
    </div>

    <!-- 2. Trestle container and loader -->
    <div
      v-if="activeLanes.length"
      class="flex-1 d-flex overflow-x-auto flex-scroll-height"
      style="position: relative"
    >
      <!-- One column per active lane -->
      <div
        v-for="lane in activeLanes"
        :key="lane.id"
        class="d-flex flex-column"
        style="min-width: 500px"
      >
        <!-- Column header with per-lane loader (like automated trestles) -->
        <div class="px-2 pb-2 font-weight-medium d-flex align-center">
          <span>{{ lane.title }}</span>
          <v-spacer />
          <v-progress-circular
            v-if="loadingLanes[lane.id]"
            color="primary"
            indeterminate
            size="18"
            width="2"
          />
        </div>

        <!-- Initial full-column loader when no trestles yet -->
        <div
          v-if="loadingLanes[lane.id] && !laneTrestles[lane.id]"
          class="flex-1 d-flex align-center justify-center pa-4"
        >
          <v-progress-circular color="primary" indeterminate />
        </div>

        <!-- Trestles list (keep showing cached data during reloads) -->
        <div
          v-else-if="laneTrestles[lane.id]?.length > 0"
          class="flex-1 mx-2 overflow-y-auto pa-4"
        >
          <div
            v-for="(trestle, idx) in laneTrestles[lane.id]"
            :key="idx"
            class="d-flex ant-border-bottom pb-2 mb-2"
          >
            <div class="d-flex align-center justify-center">
              {{ idx + 1 }}
            </div>

            <div class="d-flex flex-column flex-1 align-end mr-2">
              <v-tooltip
                v-for="module in [
                  ...new Set(
                    trestle.positions
                      .flatMap((p) => p.elements)
                      .map((e) => e?.module_id)
                  ),
                ]"
                :key="module"
                left
              >
                <template #activator="{ on }">
                  <v-chip
                    :color="getModuleColor(module, trestle)"
                    :style="{
                      opacity:
                        selectedModule !== null
                          ? selectedModule === module
                            ? 1
                            : 0.3
                          : 1,
                    }"
                    class="mb-1"
                    small
                    @click="toggleModule(module)"
                    v-on="on"
                  >
                    {{ module }}
                  </v-chip>
                </template>
                <span>{{ getTypeByModuleId(module, trestle) }}</span>
              </v-tooltip>
            </div>

            <base-dhme-double-trestle
              :selected-module="selectedModule"
              :trestle="trestle"
              :type-color-dictionary="typeColorDictionary"
            />
          </div>
        </div>

        <!-- No trestles fallback when not loading -->
        <div v-else class="flex-1 d-flex align-center justify-center pa-4">
          <span>No trestles available for this lane.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { executeCustomModuleCall } from '@/services/api/module.api';
import {
  getModuleColor,
  getModuleTypeByElements,
} from '@/modules/daiwa-house-modular-europe/AssemblyHallDashboard/assembly-hall-trestles.utils';
import BaseDhmeDoubleTrestle from '@/components/Modules/Daiwa-House-Modular-Europe/Base/BaseDhmeDoubleTrestle.vue';

import { queryTablesV2 } from '@/services/api/v2/connect_v2';
import ColorHelper from '@/services/color-helper';

export default {
  name: 'ManualTrestleOverview',
  components: { BaseDhmeDoubleTrestle },
  props: {
    selectedProject: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      assemblyLanes: [],
      assemblyLocations: [],
      typeColorDictionary: {},
      modules: [],
      selectedModule: null,
      selectedLaneIds: [],
      laneTrestles: {},
      exportLoading: false,
      loadingLanes: {},
    };
  },
  computed: {
    ...mapGetters({
      project: 'project',
      focusedHall: 'dhmeAssemblyHallDashboardStore/focusedHall',
      moduleId: 'dhmeAssemblyHallDashboardStore/moduleId',
      lanes: 'dhmeAssemblyHallDashboardStore/hallLanes',
    }),
    activeLanes() {
      return this.assemblyLanes.filter((lane) =>
        this.selectedLaneIds.includes(lane.id)
      );
    },
  },
  watch: {
    selectedProject: {
      immediate: true,
      async handler(value) {
        this.assemblyLocations = await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'fetchHallLocations',
          {
            hall: this.focusedHall.id,
            project: value.id,
          }
        );

        const { lanes } = await queryTablesV2({
          tables: [
            {
              name: 'CFFA_DHME_ASSEMBLY_LANES',
              project: this.project.id,
              as: 'lanes',
              sortBy: 'order',
              records: this.assemblyLocations.map((x) => x.assembly_lane),
              columns: [
                {
                  name: 'title',
                },
                {
                  name: 'color',
                },
                {
                  name: 'order',
                },
              ],
            },
          ],
        });

        this.assemblyLanes = lanes.records;
        if (this.assemblyLanes.length > 0) {
          const firstLaneId = this.assemblyLanes[0].id;
          this.selectedLaneIds = [firstLaneId];
          await this.loadTrestlesForLane(firstLaneId);
        }
      },
    },
  },
  methods: {
    async loadTrestlesForLane(laneId) {
      // Always allow reload when lane is (re)selected, but guard against double-click
      if (!laneId || this.loadingLanes[laneId]) return;

      this.$set(this.loadingLanes, laneId, true);

      try {
        const locationsByLane = this.assemblyLocations.filter(
          (l) => l.assembly_lane === laneId
        );
        const trestles = await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'fetchDoubleTrestles',
          {
            project: this.selectedProject.id,
            locations: locationsByLane.map((l) => l.id),
          }
        );

        this.$set(this.laneTrestles, laneId, trestles);
        this.updateColorDictionary(trestles);
      } catch (err) {
        this.$store.commit('showNotification', {
          content: 'Failed to load trestles for this lane.',
          color: 'error',
        });
      } finally {
        this.$set(this.loadingLanes, laneId, false);
      }
    },

    async requestManualExport() {
      this.exportLoading = true;
      try {
        // Create lanes array with title, id, and locations
        const lanesWithLocations = this.assemblyLanes.map((lane) => ({
          id: lane.id,
          title: lane.title,
          locations: this.assemblyLocations
            .filter((location) => location.assembly_lane === lane.id)
            .map((location) => location.id),
        }));

        await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'requestDoubleTrestleExport',
          {
            project: this.selectedProject.id,
            lanes: lanesWithLocations,
          }
        );
        this.$store.commit('showNotification', {
          content: 'Manual trestle export requested.',
          color: 'info',
        });
      } catch (err) {
        this.$store.commit('showNotification', {
          content: 'Failed to export trestles.',
          color: 'error',
        });
      } finally {
        this.exportLoading = false;
      }
    },

    updateColorDictionary(trestles) {
      const allElems = trestles.flatMap((t) =>
        t.positions.flatMap((p) => p.elements)
      );
      const types = [...new Set(allElems.map((e) => e.module_type))];
      const newTypes = types.filter((t) => !(t in this.typeColorDictionary));

      const newDict = {};
      newTypes.forEach((type) => {
        newDict[type] = ColorHelper.generateLightColorHex();
      });

      this.typeColorDictionary = {
        ...this.typeColorDictionary,
        ...newDict,
      };
    },
    toggleModule(module) {
      this.selectedModule = this.selectedModule === module ? null : module;
    },

    getModuleColor(moduleId, trestle) {
      return getModuleColor(moduleId, trestle, this.typeColorDictionary);
    },

    getTypeByModuleId(moduleId, trestle) {
      return getModuleTypeByElements(moduleId, trestle);
    },
  },
};
</script>

<style lang="scss" scoped>
.double-trestle-container {
  width: 200px;
  border-bottom: solid 2px #6b6b6b;
  border-top: solid 2px #6b6b6b;
  display: flex;
  flex-direction: column;
  background-color: rgba(255, 255, 255, 0.75);

  .manual-trestle-separator {
    background-color: #6b6b6b;
    height: 10px;
    width: 100%;
  }
}

.header-row {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.header-toggles-wrapper {
  max-width: 100%;
  overflow-x: auto;
  flex: 1 1 auto;
}

/* prevent the export button from shrinking out of view */
.flex-shrink-0 {
  flex-shrink: 0;
}
</style>
